import React, { useEffect, useRef, useState } from 'react';
import { View, Keyboard, ScrollView, Text, Image, TouchableOpacity, ActivityIndicator } from 'react-native-web';
import { toast } from 'react-hot-toast';
import moment from 'moment';
import { cloneDeep } from 'lodash';
import { useRoute } from '@react-navigation/native';
import FeatherIcon from 'react-native-vector-icons/dist/Feather';
import * as Animatable from 'react-native-animatable';
import Lottie from 'react-lottie';

// Custom Components
import { ChatItem } from '../../components/component_chat_item';
import { Button, NavBar, TextBox } from '../../components';
import LoadingView from '../../components/component_loading_view';

// Assets
import * as VendorChatLoading from '../../lotties/lottie_chat_loading.json';

// Utils
import { showAlert, showAppError } from '../../utils/util_helpers';

// APIs
import { deleteTopicComment, editTopicComment, getTopicComment, markCommentsAsRead, markTopicAsRead, markTopicFilled, postTopicComment } from '../../modules/api_forum';

// Configs
import GLOBAL from '../../configs/config_global';

// Navigation
import * as RootNavigation from '../../navigation/RootNavigation';
import RouteKeys from '../../navigation/RouteKeys';

export function ChatDetails() {
    const {
        topicData: dataFromRoute = {},
        onNewReplyPosted = () => {},
        onReplyDeleted = () => {},
        onCommentsRead = () => {},
        onTopicUpdated = () => {}
    } = useRoute()?.params || {};

    const isTopicCreator = (dataFromRoute['author'].id === GLOBAL.DATA.USER.id);
    const REPLIES_PER_PAGE = 10;

    // region Refs
    const contentScrollView = useRef(null);
    const replyInput = useRef(null);
    // endregion

    // region Local State
    const [topicData, setTopicData] = useState(dataFromRoute);

    const [editingReplyText, setEditingReplyText] = useState('');

    const [isLoadingReplies, setLoadingReplies] = useState(false);
    const [replyData, setReplyData] = useState([]);
    const [isPostingReply, setPostingReply] = useState(false);

    const [currentPage, setCurrentPage] = useState(0);
    const [lastPage, setLastPage] = useState(0);
    const [totalReplies, setTotalReplies] = useState(0);

    const [isMarkingFilled, setMarkingFilled] = useState(false);

    const [isDeletingReply, setDeletingReply] = useState(false);
    const [isUpdatingReply, setUpdatingReply] = useState(false);

    const [selectedReplyItem, setSelectedReplyItem] = useState(null);
    // endregion

    // region Component Mounting
    useEffect(() => {
        goFetchReplies();
        markTopicAsRead(topicData.id).then(() => {})
    }, []);
    // endregion

    // region Function - Fetch Replies
    const goFetchReplies = () => {
        setLoadingReplies(true);
        getTopicComment(topicData.id, 1, REPLIES_PER_PAGE)
            .then((lastResult) => {
                setReplyData(lastResult['data']);
                setCurrentPage(lastResult['currentPage']);
                setLastPage(lastResult['lastPage']);
                setTotalReplies(lastResult['totalResult']);

                // Mark all comments as read
                markCommentsAsRead(topicData.id).then(() => onCommentsRead());
            })
            .catch((error) => showAppError(error))
            .finally(() => setLoadingReplies(false))
    }

    const goFetchMoreReplies = () => {
        setLoadingReplies(true);
        getTopicComment(topicData.id, currentPage + 1, REPLIES_PER_PAGE)
            .then((result) => {
                setReplyData(replyData.concat(result['data']));
                setCurrentPage(result['currentPage']);
                setLastPage(result['lastPage']);
                setTotalReplies(result['totalResult']);
            })
            .catch((error) => showAppError(error))
            .finally(() => setLoadingReplies(false));
    }
    // endregion

    // region Function - Post Reply
    const composeNewReply = () => {
        showAlert({
            text: 'Enter your reply',
            showTextInput: true,
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Send Reply',
                color: GLOBAL.COLOR.MAIN,
                onPress: (text) => goPostReply(text)
            }
        })
    };

    const goPostReply = (text) => {
        if (!text.trim()) return;

        setPostingReply(true);
        postTopicComment(topicData.id, text)
            .then((result) => {
                setReplyData(replyData.concat(result));
                const newReplyCount = totalReplies + 1;
                setTotalReplies(newReplyCount);
                setTimeout(() => contentScrollView.current?.scrollToEnd(), 250);

                onNewReplyPosted(newReplyCount);
            })
            .catch((error) => showAppError(error))
            .finally(() => setPostingReply(false));
    }
    // endregion

    // region Function - Edit Reply
    const editReply = (replyItem) => {
        setSelectedReplyItem(replyItem);
        setEditingReplyText(replyItem.description?.trim() || '');
        setTimeout(() => replyInput.current?.focus(), 250);
    }

    const goEditReply = () => {
        Keyboard.dismiss();

        setUpdatingReply(true);
        setTimeout(() => {
            editTopicComment(topicData.id, selectedReplyItem.id, editingReplyText)
                .then((result) => {
                    const tempReplyData = cloneDeep(replyData);
                    const replyIndex = tempReplyData.findIndex((item) => item.id === selectedReplyItem.id);

                    tempReplyData.splice(replyIndex, 1, result);

                    setSelectedReplyItem(null);
                    setReplyData(tempReplyData);

                    // Notify user
                    toast('Reply updated successfully');
                })
                .catch((error) => showAppError(error))
                .finally(() => setUpdatingReply(false));
        }, 1000);
    }
    // endregion

    // region Function - Remove Reply
    const goRemoveReply = (replyItem) => {
        showAlert({
            text: `Are you sure you want to remove this reply?<br/><br/>"${replyItem.description?.trim()}"`,
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                color: 'red',
                onPress: () => {
                    setDeletingReply(true);
                    setTimeout(() => {
                        deleteTopicComment(topicData.id, replyItem.id)
                            .then(() => {
                                const tempReplyData = cloneDeep(replyData);
                                const replyIndex = tempReplyData.findIndex((item) => item.id === replyItem.id);

                                tempReplyData.splice(replyIndex, 1);

                                // Set data to state
                                const newReplyCount = totalReplies - 1;
                                setTotalReplies(newReplyCount);
                                setReplyData(tempReplyData);

                                // Pass data back to parent scene
                                onReplyDeleted(newReplyCount);

                                // Notify user
                                toast('Reply removed successfully');
                            })
                            .catch((error) => showAppError(error))
                            .finally(() => setDeletingReply(false));
                    }, 1000);
                }
            }
        })
    }
    // endregion

    // region Function - Go Mark As Filled
    const goMarkAsFilled = () => {
        showAlert({
            text: 'Confirm marking this event as filled?',
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                color: 'red',
                onPress: () => {
                    setMarkingFilled(true);
                    markTopicFilled(topicData.id, null)
                        .then((updatedTopicData) => {
                            const newTopicData = Object.assign(cloneDeep(topicData), updatedTopicData);
                            setTopicData(newTopicData);
                            onTopicUpdated(cloneDeep(newTopicData));
                        })
                        .catch((error) => showAppError(error))
                        .finally(() => setMarkingFilled(false));
                }
            }
        })
    }
    // endregion

    // region On Edit Topic Pressed
    const _onEditTopicPressed = () => {
        RootNavigation.navigate(
            RouteKeys.CHAT_NEW,
            {
                topicData,
                onNewTopicCreated: (newTopic) => {
                    setTopicData(newTopic);
                    onTopicUpdated(newTopic);
                },
            }
        )
    }
    // endregion

    // region Render Reply Item
    const renderReplyItem = (item, index) => {
        const isLastItem = index === (replyData.length - 1);
        const isMyReply = GLOBAL.DATA.USER['id'] === (item['author']?.id || -1);

        const isFillInTruck = topicData['filled_trucks']?.find((truck) => truck.id === item.author['food_truck']?.id);

        const anotherReplySelected = !!selectedReplyItem && (selectedReplyItem.id !== item.id);
        const thisReplySelected = !!selectedReplyItem && (selectedReplyItem.id === item.id);

        return(
            <View
                key={`replyItem_${item.id}`}
                pointerEvents={anotherReplySelected ? 'none' : 'auto'}
                style={{ width: '100%', backgroundColor: 'transparent', flexDirection: 'row', paddingHorizontal: 24, opacity: anotherReplySelected ? 0.3 : 1 }}>
                {/* Line */}
                <View style={{ width: 64, height: '100%', opacity: (thisReplySelected || anotherReplySelected) ? 0.3 : 1 }}>
                    <View style={{ width: 4, height: isLastItem ? 64 : '100%', backgroundColor: GLOBAL.COLOR.BASIC.COVER_GRAY, alignSelf: 'center' }} />
                    <View style={{
                        position: 'absolute', right: 0, top: 60,
                        height: 4, width: 32, backgroundColor: GLOBAL.COLOR.BASIC.COVER_GRAY
                    }} />
                </View>

                {/* Main Reply Content */}
                <View style={{ flex: 1, marginTop: 24, backgroundColor: 'white', borderRadius: 8, padding: 16, flexDirection: 'row', alignItems: 'center', overflow: 'hidden' }}>
                    {/* Content */}
                    <View style={{ flex: 1 }}>
                        {/* User info and rating */}
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            {/* User Avatar */}
                            <View style={{ width: 40, height: 40, borderRadius: 20, overflow: 'hidden', backgroundColor: GLOBAL.COLOR.TEXT_GRAY }}>
                                <Image
                                    source={item.author.avatar ? { uri: item.author.avatar } : require('../../images/image_user_placeholder_round.png')}
                                    style={{ width: 40, height: 40 }}
                                    resizeMode={item.author.avatar ? 'cover' : 'contain'}
                                />
                            </View>

                            {/* Name and submit date */}
                            <View style={{ flex: 1, marginLeft: 16 }}>
                                {/* User Name */}
                                <Text style={{ color: isMyReply ? GLOBAL.COLOR.BASIC.UI_BLUE : GLOBAL.COLOR.BASIC.BLACK_TWO, fontWeight: '500', fontSize: 16 }}>
                                    {item.author?.food_truck?.name || item.food_truck?.name || item.author?.name}{isMyReply && ' (You)'}
                                </Text>

                                {/* Submitted Date */}
                                <Text style={{ color: GLOBAL.COLOR.BASIC.PINKISH_GREY, marginTop: 4, fontSize: 15 }}>
                                    {moment.utc(item['created_at']).fromNow()}
                                </Text>
                            </View>

                            {/* Fill-In Truck Notice */}
                            {
                                isFillInTruck &&
                                <View style={{
                                    position: 'absolute', right: 20, top: 20, backgroundColor: GLOBAL.COLOR.BASIC.UI_GREEN,
                                    paddingHorizontal: 12, paddingVertical: 8, borderRadius: 6
                                }}>
                                    <Text style={{ fontWeight: 'bold', color: 'white', fontSize: 14, letterSpacing: 0.4 }}>
                                        Fill-In Truck
                                    </Text>
                                </View>
                            }
                        </View>

                        {/* Comment Content */}
                        {
                            thisReplySelected ?
                                <TextBox
                                    ref={replyInput}
                                    style={{ marginTop: 24 }}
                                    value={editingReplyText}
                                    onChangeText={setEditingReplyText}
                                    title={'Editing Reply'}
                                    placeholder={'Editing Reply'}
                                />
                                :
                                <Text style={{ fontSize: 18, lineHeight: 28, color: GLOBAL.COLOR.BASIC.WARM_GREY, alignSelf: 'stretch', marginTop: 24 }}>
                                    {item.description?.trim()}
                                </Text>
                        }
                    </View>

                    {/* Edit Button */}
                    {
                        (isMyReply && !thisReplySelected) &&
                        <TouchableOpacity
                            onPress={() => editReply(item)}
                            style={{ width: 64, height: 64, alignItems: 'center', justifyContent: 'center' }}>
                            <FeatherIcon name={'edit'} color={GLOBAL.COLOR.TEXT_GRAY} size={24} />
                        </TouchableOpacity>
                    }

                    {/* Delete Button */}
                    {
                        (isMyReply && !thisReplySelected) &&
                        <TouchableOpacity
                            onPress={() => goRemoveReply(item)}
                            style={{ width: 64, height: 64, alignItems: 'center', justifyContent: 'center' }}>
                            <FeatherIcon name={'trash'} color={GLOBAL.COLOR.BASIC.RED} size={24} />
                        </TouchableOpacity>
                    }

                    {/* Confirm / Cancel Edit Buttons */}
                    {
                        thisReplySelected &&
                        <View style={{ position: 'absolute', top: 16, right: 16, flexDirection: 'row', alignItems: 'center' }}>
                            {/* Confirm */}
                            <Button
                                onPress={() => goEditReply()}
                                text={'Confirm'}
                                textStyle={{ color: 'white', fontSize: 14, marginLeft: 16, marginRight: 16 }}
                                style={{ height: 40, marginRight: 8 }}
                                locked={editingReplyText?.trim()?.toLowerCase()?.localeCompare(selectedReplyItem?.description?.trim()?.toLowerCase()) === 0}
                            />

                            {/* Cancel */}
                            <Button
                                onPress={() => {
                                    setEditingReplyText('');
                                    setSelectedReplyItem(null);
                                }}
                                text={'Cancel'}
                                textStyle={{ color: 'white', fontSize: 14, marginLeft: 16, marginRight: 16 }}
                                style={{ height: 40, backgroundColor: GLOBAL.COLOR.BASIC.GRAY }}
                            />
                        </View>
                    }

                    {/* Edit Loading */}
                    {
                        (thisReplySelected && isUpdatingReply) &&
                        <Animatable.View
                            animation={'fadeIn'} duration={600}
                            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', backgroundColor: 'rgba(0, 0, 0, 0.7)' }}>
                            <ActivityIndicator color={'white'} size={24} />

                            {/* Text */}
                            <Text style={{ color: 'white', fontSize: 16, fontWeight: 'bold', marginLeft: 16 }}>
                                Updating your reply
                            </Text>
                        </Animatable.View>
                    }
                </View>
            </View>
        );
    }
    // endregion


    // region Render Empty Reply View
    const renderEmptyReplyView = () => {
        return(
            <Animatable.View
                animation={'fadeIn'}
                style={{ flex: 1, width: '100%', alignItems: 'center', paddingVertical: 64, paddingLeft: 88 }}>
                <Image
                    source={require('../../images/iconLineForum@3x.png')}
                    style={{ width: 64, height: 64, tintColor: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY }}
                    resizeMode={'contain'}
                />

                <Text style={{ fontWeight: '500', color: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY, fontSize: 18, marginTop: 16 }}>
                    No Replies Yet
                </Text>

                <Text style={{ fontSize: 15, color: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY, marginTop: 8 }}>
                    Be the first to join the conversation
                </Text>
            </Animatable.View>
        );
    }
    // endregion

    // region MAIN RENDER
    const isMyTopic = GLOBAL.DATA.USER['id'] === (topicData['author']?.id || -1);
    return(
        <View style={{ width: '100%', flex: 1 }}>
            {/* Nav Bar */}
            <NavBar
                leftButtonOnPress={() => RootNavigation.goBack()}
                title={'Topic Details'}
                leftButtonIconName={'arrow-back'}
                rightButtonIconName={isMyTopic ? 'edit' : undefined}
                rightButtonColor={GLOBAL.COLOR.TEXT_GRAY}
                rightButtonOnPress={isMyTopic ? _onEditTopicPressed : undefined}
            />

            {/* Main Content */}
            <View style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}>
                {/* Big ScrollView */}
                <ScrollView
                    ref={contentScrollView}
                    style={{ width: '100%', flex: 1, backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}
                    contentContainerStyle={{ paddingBottom: 128 }}>
                    {/* Review Content */}
                    <View style={{
                        alignSelf: 'stretch', marginHorizontal: 24, marginTop: 24,
                        backgroundColor: 'white', borderWidth: 1, borderRadius: 6, borderColor: GLOBAL.COLOR.DIVIDER,
                        overflow: 'hidden'
                    }}>
                        <ChatItem item={topicData} isInDetail={true} />

                        {/* Mark Filled Button */}
                        {
                            ((Number(topicData['fill_in_needed']) === 1) && (Number(topicData['is_filled']) === 0) && isTopicCreator) &&
                            <Button
                                onPress={goMarkAsFilled}
                                style={{ backgroundColor: GLOBAL.COLOR.BASIC.UI_GREEN, width: '100%', borderRadius: 0 }}
                                textStyle={{ color: 'white' }}
                                text={'Mark as Filled'}
                                isLoading={isMarkingFilled}
                                loadingText={'Marking this topic as Filled'}
                            />
                        }
                    </View>

                    {/* Reply List */}
                    {
                        (isLoadingReplies && currentPage === 0) ?
                            <View style={{ width: '100%', flex: 1, alignItems: 'center', justifyContent: 'center', paddingLeft: 88 }}>
                                <Lottie
                                    options={{
                                        loop: true,
                                        autoplay: true,
                                        animationData: VendorChatLoading,
                                        rendererSettings: { preserveAspectRatio: 'xMidYMid slice' }
                                    }}
                                    width={240}
                                    height={240}
                                    isStopped={false}
                                    isPaused={false}
                                />
                            </View>
                            :
                            <View style={{ width: '100%', flex: 1, backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}>
                                {/* Reply List */}
                                {
                                    replyData.length > 0 ?
                                        replyData.map((replyItem, index) => renderReplyItem(replyItem, index))
                                        :
                                        (isLoadingReplies ? false : renderEmptyReplyView())
                                }

                                {/* Send Reply Button */}
                                {
                                    (isLoadingReplies ? (currentPage > 0) : true) &&
                                    <View style={{
                                        width: '100%', flexDirection: 'row', alignItems: 'center',
                                        paddingLeft: 88, paddingRight: 24, paddingTop: 24
                                    }}>
                                        {/* Load More Replies Button */}
                                        {
                                            (currentPage < lastPage) &&
                                            <Button
                                                onPress={() => goFetchMoreReplies()}
                                                text={'Load more replies'}
                                                textStyle={{ color: 'white', fontSize: 16 }}
                                                style={{ flex: 1, marginRight: 8 }}
                                                image={'refresh'}
                                                imageSize={20}
                                                imageNextToText={true}
                                                loadingText={isLoadingReplies ? 'Fetching more replies...' : `Load more topics (${totalReplies - (currentPage * REPLIES_PER_PAGE)} remaining)`}
                                                isLoading={isLoadingReplies}
                                                locked={!!selectedReplyItem}
                                            />
                                        }
                                    </View>
                                }
                            </View>
                    }
                </ScrollView>

                {/* Send Reply Button */}
                {
                    (!isLoadingReplies && currentPage > 0) &&
                    <Button
                        onPress={() => composeNewReply()}
                        text={'Send new reply'}
                        textStyle={{ color: 'white', fontSize: 16 }}
                        style={{ position: 'absolute', bottom: 40, right: 40, backgroundColor: GLOBAL.COLOR.BASIC.UI_BLUE }}
                        image={'reply'}
                        imageSize={20}
                        imageNextToText={true}
                        loadingText={'Sending your reply'}
                        isLoading={isPostingReply}
                        locked={!!selectedReplyItem}
                    />
                }
            </View>

            {/* Loading View */}
            {isDeletingReply && <LoadingView isAbsolute={true}/>}
        </View>
    );
    // endregion
}
